import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import StarIcon from "@mui/icons-material/Star";
import StarOutlineIcon from "@mui/icons-material/StarOutline";

import JarvisButton from "components/JarvisButton";
import Loader from "components/common/Loader";
import { masterApis } from "stores/master/masterApis";
import { dateTimeHelper } from "helper/dateTime";
import { commonActions } from "stores/common/commonActions";
import AppBar from "components/common/AppBar";
import ToolBar from "components/common/ToolBar";
import Text from "components/common/Text";
import CallScheduler from "components/common/callScheduler";
import { masterActions } from "stores/master/masterActions";

const RATING_LABELS = {
  1: "Very Poor",
  2: "Poor",
  3: "Good",
  4: "Very Good",
  5: "Excellent",
};

const PROGRESS_CONFIG = [
  { width: "33%", text: "1 of 3" },
  { width: "33%", text: "1 of 3" },
  { width: "66%", text: "2 of 3" },
  { width: "100%", text: "3 of 3" },
];

// ---------------------------------------------
// Custom Hook
// ---------------------------------------------
const useFeedback = () => {
  const [step, setStep] = useState(1);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [overallRating, setOverallRating] = useState(0);
  const [overallReview, setOverallReview] = useState("");
  const [aspectRatings, setAspectRatings] = useState({});
  const [thankTitle, setThankTitle] = useState("");
  const [thankDesc, setThankDesc] = useState("");
  const [averageRating, setAverageRating] = useState();
  const [showErrors, setShowErrors] = useState(false);

  const history = useHistory();
  const dispatch = useDispatch();
  const reviewCount = useSelector(
    (state) => state.customer.profile.reviewCount
  );

  const next = () => setStep((prev) => prev + 1);
  const back = () => setStep((prev) => prev - 1);

  const findQuestion = (type) =>
    questions.find(
      (q) => String(q.questionType).trim().toUpperCase() === type.toUpperCase()
    );

  const fetchQuestions = async () => {
    setLoading(true);
    try {
      const res = await masterApis.getFeedbackQuestions();
      const qList =
        res?.data?.questions ??
        res?.data?.data?.questions ??
        res?.data?.result?.questions ??
        res?.questions ??
        [];

      setQuestions(qList);

      const specificQuestion = qList.find(
        (q) => String(q.questionType).trim().toUpperCase() === "SPECIFIC"
      );

      if (specificQuestion?.subQuestions) {
        const initialRatings = {};
        specificQuestion.subQuestions.forEach((sq) => {
          initialRatings[sq.questionId] = 0;
        });
        setAspectRatings(initialRatings);
      }
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  const validateAspectRatings = () =>
    Object.values(aspectRatings).every((rating) => rating > 0);

  const buildSurveyPayload = () => {
    const specificQuestion = findQuestion("SPECIFIC");
    if (!specificQuestion?.subQuestions) return [];

    return specificQuestion.subQuestions.map((sq) => ({
      question: sq.questionText,
      rating: aspectRatings[sq.questionId] || 0,
    }));
  };

  const handleSubmit = async () => {
    setShowErrors(true);

    if (!validateAspectRatings()) {
      dispatch(
        commonActions.setToast({
          message: "Please rate all specific areas before submitting.",
        })
      );
      return;
    }

    const finalPayload = {
      reviewStatus: "YES",
      data: {
        overallRating,
        overallReview,
        survey: buildSurveyPayload(),
      },
    };

    setLoading(true);
    try {
      const res = await masterApis.submitFeedback(finalPayload);
      setThankTitle(res.title || "Thank You!");
      setThankDesc(res.desc || "Your review was submitted successfully.");
      setAverageRating(res.averageRating || "");

      next();
    } catch (error) {
      console.error("Submit error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleScheduleCall = async (data) => {
    const payload = {
      scheduleDate: dateTimeHelper.sipDate(new Date(data.date)),
      scheduleTime: data.time.slice(0, 5),
    };

    try {
      setLoading(true);

      await masterApis.submitScheduleCall(payload);

      // After scheduling, redirect
      history.push("/");
    } catch (error) {
      console.error("Schedule error:", error);
      dispatch(
        commonActions.setToast({
          message: "Unable to schedule call. Try again.",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const backHandler = async () => {
    const payload = { reviewStatus: "NO" };
    try {
      await masterApis.submitFeedback(payload);
    } catch (error) {
      console.log("API call error:", error);
    }
    history.push("/");
  };

  return {
    step,
    questions,
    loading,
    overallRating,
    overallReview,
    aspectRatings,
    thankTitle,
    thankDesc,
    reviewCount,
    averageRating,
    setOverallRating,
    setOverallReview,
    setAspectRatings,
    next,
    back,
    fetchQuestions,
    handleSubmit,
    backHandler,
    findQuestion,
    setAverageRating,
    handleScheduleCall,
    showErrors,
  };
};

// ---------------------------------------------
// UI Components
// ---------------------------------------------
const StarRating = ({
  rating,
  onRatingChange,
  readonly = false,
  size = 38,
}) => {
  const StarComponent = readonly ? StarIcon : StarOutlineIcon;

  return (
    <div
      style={{
        background: "#FFF",
        padding: 20,
        borderRadius: 12,
        border: "1px solid #E5E7EB",
        marginTop: 20,
        textAlign: "center",
      }}
    >
      <div style={{ display: "flex", justifyContent: "center", gap: 8 }}>
        {[1, 2, 3, 4, 5].map((star) => (
          <span
            key={star}
            style={{
              fontSize: size,
              cursor: readonly ? "default" : "pointer",
              color:
                star <= rating ? "#22C55E" : readonly ? "#CBD5E1" : "#D1D5DB",
            }}
            onClick={() => !readonly && onRatingChange(star)}
          >
            <StarComponent />
          </span>
        ))}
      </div>
      {!readonly && (
        <p style={{ marginTop: 8, fontSize: 13, color: "#6B7280" }}>
          Tap to rate
        </p>
      )}
    </div>
  );
};

const AspectRating = ({ label, value, onChange, aspectKey, showError }) => {
  const [circleSize, setCircleSize] = useState(32);
  const [fontSize, setFontSize] = useState(22);

  useEffect(() => {
    const handleResize = () => {
      const width = window.screen.width;
      if (width < 360) {
        setCircleSize(24);
        setFontSize(16);
      } else if (width < 430) {
        setCircleSize(28);
        setFontSize(18);
      } else {
        setCircleSize(32);
        setFontSize(22);
      }

      // console.log("width", width);
    };

    handleResize(); // initial sizing
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        border: showError ? "1.5px solid #EF4444" : "1px solid #E5E7EB",
        borderRadius: 12,
        background: "#FFF",
        marginBottom: 14,
        padding: "16px 16px 16px 20px",
        position: "relative",
        flexWrap: "wrap",
      }}
    >
      <div
        style={{
          position: "absolute",
          left: 0,
          top: 14,
          width: 4,
          height: circleSize,
          background: showError ?  "#EF4444" : "#F4C430",
          borderRadius: "4px",
        }}
      />

      <div style={{ flex: 1, minWidth: 80 }}>
        <p style={{ margin: 0, fontWeight: 600, fontSize }}>{label}</p>
      </div>

      <div
        style={{
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          justifyContent: "flex-end",
          maxWidth: "100%",
        }}
      >
        {[1, 2, 3, 4, 5].map((rating) => (
          <div
            key={rating}
            onClick={() => onChange(aspectKey, rating)}
            style={{
              width: circleSize,
              height: circleSize,
              borderRadius: "50%",
              border:
                value === rating ? "2px solid #2563EB" : "1px solid #D1D5DB",
              background: value === rating ? "#EAF2FF" : "#FFF",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              cursor: "pointer",
              fontSize: circleSize / 2.5,
              fontWeight: 600,
            }}
          >
            {rating}
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------------------------------------------
// Steps
// ---------------------------------------------
const Step1 = ({ overallRating, onRatingChange, starQuestion }) => (
  <>
    <h3>{starQuestion?.questionText || "Rate Your Experience"}</h3>
    <p style={{ color: "#6B7280" }}>
      {starQuestion?.aspectKey || "How has your experience been so far?"}
    </p>
    <StarRating rating={overallRating} onRatingChange={onRatingChange} />
  </>
);

const Step2 = ({ overallRating, starQuestion }) => (
  <>
    <h3>{starQuestion?.questionText || "Rate Your Experience"}</h3>
    <p style={{ color: "#6B7280" }}>
      {starQuestion?.aspectKey || "How has your experience been?"}
    </p>
    <StarRating rating={overallRating} readonly />
    <p style={{ marginTop: 8, color: "#808591", textAlign: "center" }}>
      <b>{overallRating}/5 • </b> {RATING_LABELS[overallRating]}
    </p>
  </>
);

const Step3 = ({
  overallReview,
  onReviewChange,
  aspectRatings,
  onAspectRatingChange,
  textQuestion,
  specificQuestion,
  showErrors,
}) => (
  <>
    <h3>{textQuestion?.questionText || "Share your thoughts"}</h3>
    <p style={{ color: "#808591" }}>
      {textQuestion?.aspectKey ||
        "Tell us what you liked and what we can improve."}
    </p>

    <textarea
      value={overallReview}
      onChange={onReviewChange}
      placeholder="Write your Feedback here…"
      style={{
        width: "100%",
        height: 90,
        padding: 20,
        borderRadius: 10,
        border: "1px solid #D1D5DB",
        marginTop: 12,
        resize: "none",
      }}
    />

    <h4 style={{ marginTop: 24 }}>
      Rate Specific Areas <span style={{ color: "#FF375F" }}>*</span>
    </h4>

    {Array.isArray(specificQuestion?.subQuestions) &&
      specificQuestion.subQuestions.map((sq) => (
        <AspectRating
          key={sq.questionId}
          label={sq.questionText}
          aspectKey={sq.questionId}
          value={aspectRatings[sq.questionId]}
          onChange={onAspectRatingChange}
          showError={showErrors && aspectRatings[sq.questionId] === 0}
        />
      ))}
  </>
);

const Step4 = ({ thankTitle, thankDesc, onContinue }) => (
  <div
    style={{
      background: "#FFF",
      padding: 20,
      borderRadius: 12,
      border: "1px solid #E5E7EB",
      textAlign: "center",
    }}
  >
    <img src="/images/thank-icon.svg" alt="Thank you" />
    <h3>{thankTitle}</h3>
    <p>{thankDesc}</p>

    <JarvisButton
      onClick={onContinue}
      disableStartIcon
      disableEndIcon
      sx={{
        width: "30%",
        background: "#2563EB !important",
        color: "#FFF !important",
      }}
    >
      Continue
    </JarvisButton>
  </div>
);

// ---------------------------------------------
// Footer
// ---------------------------------------------
const FooterButtons = ({ step, overallRating, onBack, onNext, onSubmit }) => {
  if (step === 4) return null;

  // Detect screen width
  const isMobile =
    typeof window !== "undefined" ? window.innerWidth < 900 : true;

  const containerStyle = {
    display: "flex",
    gap: 12,
    width: "100%",
    justifyContent: isMobile ? "center" : "flex-start", // center on mobile, left on desktop
  };

  const buttonStyle = {
    width: "30%", // default 30%
  };

  const submitButtonStyle = {
    width: isMobile ? "55%" : "55%", // Submit Review wider on mobile
  };

  if (step === 1) {
    return (
      <div style={containerStyle}>
        <JarvisButton
          disabled={overallRating === 0}
          onClick={onNext}
          disableStartIcon
          disableEndIcon
          sx={{
            ...buttonStyle,
            background:
              overallRating === 0 ? "#CFD6E2 !important" : "#2563EB !important",
            color: "#FFF !important",
          }}
        >
          Continue
        </JarvisButton>
      </div>
    );
  }

  if (step === 2) {
    return (
      <div style={containerStyle}>
        <JarvisButton
          variant="outlined"
          onClick={onBack}
          disableStartIcon
          disableEndIcon
          sx={{
            ...buttonStyle,
            background: "#FFF !important",
            border: "1px solid #D1D5DB !important",
            color: "#6B7280 !important",
          }}
        >
          Back
        </JarvisButton>

        <JarvisButton
          disabled={overallRating === 0}
          onClick={onNext}
          disableStartIcon
          disableEndIcon
          sx={{
            ...buttonStyle,
            background:
              overallRating === 0 ? "#CFD6E2 !important" : "#2563EB !important",
            color: "#FFF !important",
          }}
        >
          Continue
        </JarvisButton>
      </div>
    );
  }

  if (step === 3) {
    return (
      <div style={containerStyle}>
        <JarvisButton
          variant="outlined"
          onClick={onBack}
          disableStartIcon
          disableEndIcon
          sx={{
            ...buttonStyle,
            background: "#FFF !important",
            border: "1px solid #D1D5DB !important",
            color: "#6B7280 !important",
          }}
        >
          Back
        </JarvisButton>

        <JarvisButton
          onClick={onSubmit}
          disableStartIcon
          disableEndIcon
          sx={{
            ...submitButtonStyle,
            background: "#2563EB !important",
            color: "#FFF !important",
          }}
        >
          Submit Review
        </JarvisButton>
      </div>
    );
  }

  return null;
};

// ---------------------------------------------
// Main Component
// ---------------------------------------------
const Feedback = () => {
  const {
    step,
    questions,
    loading,
    overallRating,
    overallReview,
    aspectRatings,
    thankTitle,
    thankDesc,
    reviewCount,
    averageRating,
    setOverallRating,
    setOverallReview,
    setAspectRatings,
    next,
    back,
    fetchQuestions,
    handleSubmit,
    backHandler,
    findQuestion,
    setAverageRating,
    handleScheduleCall,
    showErrors,
  } = useFeedback();

  const history = useHistory();

  useEffect(() => {
    fetchQuestions();
  }, []);

  const handleAspectRatingChange = (aspectKey, rating) => {
    setAspectRatings((prev) => ({ ...prev, [aspectKey]: rating }));
  };

  const progressConfig = PROGRESS_CONFIG[step - 1];

  const starQuestion = findQuestion("STAR");
  const textQuestion = findQuestion("TEXT");
  const specificQuestion = findQuestion("SPECIFIC");

  return (
    <>
      <Loader loading={loading} position="fixed" size="md" />

      {/* RESPONSIVE FIX */}
      <style>{`
        .feedback-wrapper {
          margin-top: 140px;
          margin-bottom: 130px;
          width: 100%;
          // max-width: 430px;
          margin-left: auto;
          margin-right: auto;
          padding: 0 16px;
          // height: 100dvh;
         // min-height: 100dvh;
        }

        .feedback-footer {
          position: fixed;
          bottom: 60px;
          left: 0;
          right: 0;
          padding: 16px 20px;
          z-index: 800;
          display: flex;
          justify-content: center;
        }

        @media (max-width: 600px) {
        .feedback-header{
        border-Bottom: none !important;
        top: 50px !important;   
      }
        .feedback-progress{ 
          top: 90px !important;
        }
      }

        @media (min-width: 900px) {
          .feedback-wrapper {
            max-width: 600px;
            padding: 0 40px;
            margin-left: 40px;
          }

          .feedback-footer {
            width: calc(50% - 80px);
            margin-left: 40px;
            justify-content: flex-start;
            padding-left: 0;
            padding-right: 0;
          }
        }
      `}</style>
      {/* HEADER */}
      <div
        style={{
          width: "100%",
          //  background: "rgba(255, 255, 255, 0.91)",
          //  backdropFilter: "blur(5px)",
          background: "white",
          paddingTop: "35px",
          padding: "22px 20px 14px",
          borderBottom: "1px solid #E5E7EB",
          position: "fixed",
          top: 0,
          zIndex: 1000,
        }}
      >
        <div
          className="feedback-header"
          style={{
            width: "100%",
            padding: "14px 20px",
            borderBottom: "1px solid #E5E7EB",
            // position: "fixed",
            top: 0,
            zIndex: 1000,
          }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            {reviewCount > 0 && (
              <span
                onClick={() => (step > 1 ? back() : backHandler())}
                style={{ cursor: "pointer", marginRight: 12 }}
              >
                <ArrowBackIcon />
              </span>
            )}
            <Text style={{ margin: 0 }}>Feedback & Experience Survey</Text>
          </div>
        </div>

        {/* PROGRESS BAR */}
        <div
          className="feedback-progress"
          style={{
            width: "100%",
            // background: "#FFF",
            padding: "12px 20px",
            // position: "fixed",
            top: 60,
            zIndex: 1900,
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <div
            style={{
              flex: 1,
              height: 6,
              background: "#E5EAF1",
              borderRadius: 40,
            }}
          >
            <div
              style={{
                width: progressConfig.width,
                height: "100%",
                background: "#3B82F6",
                borderRadius: 40,
                transition: "0.2s",
              }}
            />
          </div>
          <p style={{ margin: 0, fontSize: 12, color: "#6B7280" }}>
            {progressConfig.text}
          </p>
        </div>
      </div>

      {/* CONTENT */}
      <div className="feedback-wrapper">
        {step === 1 && (
          <Step1
            overallRating={overallRating}
            onRatingChange={setOverallRating}
            starQuestion={starQuestion}
          />
        )}

        {step === 2 && (
          <Step2 overallRating={overallRating} starQuestion={starQuestion} />
        )}

        {step === 3 && (
          <Step3
            overallReview={overallReview}
            onReviewChange={(e) => setOverallReview(e.target.value)}
            aspectRatings={aspectRatings}
            onAspectRatingChange={handleAspectRatingChange}
            textQuestion={textQuestion}
            specificQuestion={specificQuestion}
            showErrors={showErrors}
          />
        )}

        {step === 4 && (
          <>
            <Step4
              thankTitle={thankTitle}
              thankDesc={thankDesc}
              onContinue={() => history.push("/")}
            />

            {/* CONDITION: IF RATING < 3 → SHOW SCHEDULER */}
            {/* {Number(averageRating) <= 3 ? ( */}
            {(averageRating ?? 0) <= 3 ? (
              <CallScheduler
                title="We’d Like to Help"
                description="We noticed your low rating. Choose a date and time for our team to contact you."
                onSubmit={handleScheduleCall}
                onSkip={() => history.push("/")}
                isShowButton={true}
              />
            ) : (
              <JarvisButton
                onClick={() =>
                  window.open(
                    "https://play.google.com/store/apps/details?id=com.neolite.jarvis.monitree&hl=en_IN",
                    "_blank"
                  )
                }
                disableStartIcon
                disableEndIcon
                sx={{
                  width: "100%",
                  background: "#2563EB !important",
                  color: "#FFF !important",
                  marginTop: "20px",
                }}
              >
                Rate Us on Play Store
              </JarvisButton>
            )}
          </>
        )}
      </div>

      {/* FOOTER BUTTONS */}
      <div className="feedback-footer">
        <FooterButtons
          step={step}
          overallRating={overallRating}
          onBack={back}
          onNext={next}
          onSubmit={handleSubmit}
        />
      </div>

      {/* BOTTOM STRIP */}
      <div
        style={{
          width: "100%",
          position: "fixed",
          bottom: 0,
          background: "#E3ECFA",
          padding: 12,
          textAlign: "center",
          borderTop: "1px solid #E5E7EB",
        }}
      >
        <p style={{ fontSize: 12, color: "#7A8693", margin: 0 }}>
          © 2025 Jarvis Invest • Your feedback means the world to us! <br />
          <span style={{ color: "#10B981" }}>•</span> Secure & Private
        </p>
      </div>
    </>
  );
};

export default Feedback;
